import { useContext, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import BorseContext from "../../context/borse";
import UserContext from "../../context/user";
import useBorseRequestUpdateMutation from "../../hooks/mutations/useBorseRequestUpdateMutation";
import { codiceFiscaleRegex, getLengthFromRegex, ibanRegex } from "../../utils/regex";
import Icon from "../icon";
import Question from "../question";
import BorseDiscardRequestModal from "./borse-discard-request-modal";
import useFocusOnFirstError from "../../hooks/useFocusOnFirstError";

const BorseStep2 = ({ discardModal, setDiscardModal }) => {
  const { userProfile } = useContext(UserContext);
  const {
    survey,
    campaign,
    formData,
    labels,
    currentRequest,
    setFormData,
    handlePrevious,
    appLang,
  } = useContext(BorseContext);
  const section = useMemo(() => survey?.definition?.sections[1], [survey]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    setFocus,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  useFocusOnFirstError(errors, setFocus);

  const formWatch = watch();
  const initializedFormData = useRef();

  useEffect(() => {
    reset({ ...formData, ...(initializedFormData.current ? initializedFormData.current : {}) });
  }, [reset, formData]);

  useEffect(() => {
    if (
      !initializedFormData.current &&
      !!section?.questions &&
      !!userProfile &&
      currentRequest?.requestForMe
    ) {
      const initializedFields = {
        [section.questions?.[0]?.id]: userProfile?.firstName,
        [section.questions?.[1]?.id]: userProfile?.lastName,
      };
      initializedFormData.current = initializedFields;
      setFormData((current) => ({ ...current, ...initializedFields }));
    }
  }, [userProfile, section, setFormData, survey, currentRequest]);

  const { mutateAsync: updateRequest, isError } = useBorseRequestUpdateMutation(
    null,
    section?.questions?.[5]?.answers?.find(
      (ans) => Number(ans.id) === Number(formWatch?.[section.questions[5].id])
    )?.nextSectionId
  );

  const driverQuestion = useMemo(() => section?.questions?.[13], [section]);

  const checkboxSelected = useMemo(
    () => formWatch[driverQuestion?.id],
    [formWatch, driverQuestion]
  );

  const additionalQuestion = useMemo(() => section?.questions?.[14], [section]);

  const onSubmit = async (values) => {
    const clearedValues = values;
    if (!values?.[driverQuestion?.id] || values?.[driverQuestion?.id]?.length === 0) {
      clearedValues[additionalQuestion?.id] = null;
    }

    await updateRequest({
      survey,
      campaignId: campaign?.id,
      request: currentRequest,
      formData,
      values: clearedValues,
      studentFirstName: values[section.questions[0].id],
      studentLastName: values[section.questions[1].id],
      //school: values[section.questions[5].id],
      school: section.questions[5]?.answers?.find(
        (ans) => ans.id === Number(values[section.questions[5].id])
      )?.label,
      email: currentRequest?.welfareEmail,
      phone: currentRequest?.welfarePhone,
      isRequestForMe: currentRequest?.requestForMe,
      lastCompletedStep: 1,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form" key={section.id} noValidate>
        {section?.questions?.slice(0, -1)?.map((question, i) => (
          <Question
            question={question}
            key={question.id}
            register={register}
            error={errors[`${question.id}`]}
            touched={touchedFields[`${question.id}`]}
            isDirty={dirtyFields[`${question.id}`]}
            control={control}
            trigger={trigger}
            labels={labels}
            pattern={i === 4 ? codiceFiscaleRegex : i === 8 ? ibanRegex : null}
            maxLength={
              i === 4
                ? getLengthFromRegex(codiceFiscaleRegex)
                : i === 8
                ? getLengthFromRegex(ibanRegex)
                : null
            }
            minLength={
              i === 4
                ? getLengthFromRegex(codiceFiscaleRegex)
                : i === 8
                ? getLengthFromRegex(ibanRegex)
                : null
            }
            value={formWatch[`${question.id}`]}
            attachments={survey?.attachments}
            readOnlyMode={currentRequest?.requestForMe && [0, 1].includes(i)}
            isInline={["Multiple choice", "Radio Self Submit"].includes(question.questionType)}
            locale={appLang?.value}
          />
        ))}
        {!!checkboxSelected && checkboxSelected.length > 0 && (
          <Question
            question={{ ...additionalQuestion, minNAnswers: 1 }}
            key={additionalQuestion.id}
            register={register}
            error={errors[`${additionalQuestion.id}`]}
            touched={touchedFields[`${additionalQuestion.id}`]}
            isDirty={dirtyFields[`${additionalQuestion.id}`]}
            control={control}
            trigger={trigger}
            labels={labels}
            value={formWatch[`${additionalQuestion.id}`]}
            attachments={survey?.attachments}
            isInline={true}
            locale={appLang?.value}
          />
        )}
        <div className="form__buttons-wrapper form__buttons-wrapper--static">
          {isError && (
            <p className="form__error text-center">
              {labels?.WELFARE_BORSE_ERROR_SAVING_REQUEST || "Error saving requests"}
            </p>
          )}
          <div className="form__buttons form__buttons--space-between">
            <button className="link" type="button" onClick={handlePrevious}>
              <Icon name="angle-down" />
              {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
            </button>
            <button className="button primary form__submit">
              {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
            </button>
          </div>
        </div>
      </form>
      <BorseDiscardRequestModal
        key={!!discardModal}
        discardModal={discardModal}
        setDiscardModal={setDiscardModal}
        formWatch={formWatch}
        lastCompletedStep={0}
      />
    </>
  );
};

export default BorseStep2;
